import React from "react"
import { observer } from "mobx-react"
import SVG from "react-inlinesvg"
import capitalize from "lodash/capitalize"

import plusCircleSvg from "../../../priv/static/svg/generic/plus-circle.svg"
import externalLinkSvg from "../../../priv/static/svg/generic/external-link.svg"
import downloadSvg from "../../../priv/static/svg/generic/download.svg"
import checkmarkSvg from "../../../priv/static/svg/generic/checkmark-yes.svg"
import chevronRightSvg from "../../../priv/static/svg/generic/chevron-right.svg"
import chevronDownSvg from "../../../priv/static/svg/generic/chevron-down.svg"
import lockSvg from "../../../priv/static/svg/generic/lock.svg"
import unlockSvg from "../../../priv/static/svg/generic/unlock.svg"

import AssignmentCollection from "./lists/collections/AssignmentCollection"
import Assignment from "./lists/models/Assignment"
import InfoTooltip from "./shared/InfoTooltip"
import SectionView from "./lists/SectionView"
import Package from "../models/Package"
import List from "../models/List"
import SearchBar from "./SearchBar"
import SectionCollection from "./lists/collections/SectionCollection"
import { Section } from "./lists/models/Section"
import { TripLeaderStatusBox, TourOperatorStatusBox } from "./lists/StatusBox"
import { put } from "@util/api"
interface Props {
  list: List
  assignments: Assignment[]
  packages: Package[]
  sections: any[]
  expandSectionId?: number
  sectionNounSingular: string
  sectionNounPlural: string
  tripPath: string
  participationsExportPath: string
  publicTripName: string
  roomListExportPath: string
  flightListExportPath: string
  listLockPath: string
  listStatusUpdatePath: string
  tripLeaderPermission: "full" | "assign_only" | "read_only"
  isTripLeader: boolean
  onlyStats: boolean
  reorderPath: string
  unnamePath: string
}

interface State {
  sectionCollection: SectionCollection
  allAssignments: AssignmentCollection
  expandedSectionIds?: Array<any>
  sectionRefs: { [sectionId: number]: React.RefObject<any> }
  allExpanded: boolean
}

@observer
export default class ListEditor extends React.Component<Props, State> {
  constructor(props) {
    super(props)

    const assignments = props.assignments.map((mp) => new Assignment(mp))
    const allAssignments = new AssignmentCollection(assignments)
    const sectionCollection = new SectionCollection(allAssignments, props.sections)

    this.state = {
      sectionCollection: sectionCollection,
      allAssignments: allAssignments,
      expandedSectionIds: [props.expandSectionId],
      sectionRefs: this.buildSectionRefs(sectionCollection),
      allExpanded: false,
    }
  }

  buildSectionRefs = (sectionCollection: SectionCollection) =>
    sectionCollection.items.reduce((acc, value) => {
      acc[value.id] = React.createRef()
      return acc
    }, {})

  render() {
    const { sectionCollection, allAssignments } = this.state
    const { onlyStats, packages, sectionNounSingular, sectionNounPlural, list, listLockPath, isTripLeader } = this.props

    let sectionsBody
    if (sectionCollection.length != 0) {
      sectionsBody = sectionCollection.items.map((section, idx) => {
        const sectionRef = this.state.sectionRefs[section.id]

        return (
          <div key={idx} ref={sectionRef}>
            <SectionView
              key={section.id}
              section={section}
              allAssignments={this.state.allAssignments}
              isExpanded={this.state.expandedSectionIds.includes(section.id)}
              onExpansionToggle={() => this.handleExpansionToggle(section)}
              onMoveUp={() => {
                sectionCollection.moveUp(section)
                put(this.props.reorderPath, { sections: sectionCollection.toJS() })
              }}
              onMoveDown={() => {
                sectionCollection.moveDown(section)
                put(this.props.reorderPath, { sections: sectionCollection.toJS() })
              }}
              sectionNounPlural={this.props.sectionNounPlural}
              sectionNounSingular={this.props.sectionNounSingular}
              tripLeaderPermission={this.props.tripLeaderPermission}
              isTripLeader={this.props.isTripLeader}
            />
          </div>
        )
      })
    } else {
      sectionsBody = (
        <div className="p-4 text-center text-gray-700 bg-gray-100 border-gray-200">
          You haven't added any {sectionNounPlural} yet. Create one using the link above and start adding passengers to it
        </div>
      )
    }

    const renderNewLink = () => {
      let { tripLeaderPermission } = this.props
      if (isTripLeader && tripLeaderPermission !== "full") {
        return ""
      } else {
        return (
          <a
            className="legacy-with-icon svg-icon link-button-component"
            href={`${window.location.pathname}/sections/new`}
            data-role="create-section-link">
            <SVG src={plusCircleSvg} className="" />
            <div>New {capitalize(this.props.sectionNounSingular)}</div>
          </a>
        )
      }
    }

    const renderStatusBox = () => {
      let {
        list: { status, tripLeaderPermission, reviewNote },
        isTripLeader,
        listStatusUpdatePath,
      } = this.props

      if (isTripLeader) {
        return (
          <TripLeaderStatusBox
            {...this.props}
            reviewNote={reviewNote}
            updatePath={listStatusUpdatePath}
            tripLeaderPermission={tripLeaderPermission}
            isTripLeader={isTripLeader}
            status={status}
          />
        )
      } else {
        return (
          <TourOperatorStatusBox
            {...this.props}
            reviewNote={reviewNote}
            updatePath={listStatusUpdatePath}
            tripLeaderPermission={tripLeaderPermission}
            isTripLeader={isTripLeader}
            status={status}
          />
        )
      }
    }

    const renderLockBox = () => {
      let {
        list: { tripLeaderPermission },
        listLockPath,
        isTripLeader,
      } = this.props
      let lockText, lockBoxTextTourOperators

      switch (tripLeaderPermission) {
        case "full":
          lockText = "Full access"
          lockBoxTextTourOperators = "Trip leaders can edit rooms and assign passengers"
          break

        case "assign_only":
          lockText = "Assignments only"
          lockBoxTextTourOperators = "Trip leaders can view the list and assign passengers to rooms"
          break
        case "read_only":
          lockText = "View only"
          lockBoxTextTourOperators = "Trip leaders can view the list but cannot modify it"
          break

        default:
          break
      }

      return (
        <div className="stack gap-4 pad-6 | border-gray-200 bg-gray-50" data-role="list-lock">
          <h3 className="row gap-1 type-heading-sm">{lockText}</h3>
          <p className="type-body-md max-inline-125">{lockBoxTextTourOperators}</p>
          {!isTripLeader && (
            <a className="legacy-with-icon svg-icon link-button-component" href={listLockPath} data-role="lock">
              Edit
            </a>
          )}
        </div>
      )
    }

    return (
      <>
        {onlyStats ? (
          <SectionStats
            sectionCollection={sectionCollection}
            allAssignments={allAssignments}
            packages={packages}
            sectionNounPlural={sectionNounPlural}
            list={list}
            listLockPath={listLockPath}
            isTripLeader={isTripLeader}
          />
        ) : (
          <div className="stack gap-4">
            <section className="stack gap-4">
              <div className="cluster gap-3">
                <h1 className="type-heading-md" data-role="header">
                  {this.props.list.name}
                </h1>
                <div className="grow-1"></div>

                <div className="cluster gap-3">
                  {this.maybeShowDownloadButton()}
                  {this.maybeShowExportButton()}
                  {renderNewLink()}
                </div>
                <SectionStats
                  sectionCollection={sectionCollection}
                  allAssignments={allAssignments}
                  packages={packages}
                  sectionNounPlural={sectionNounPlural}
                  list={list}
                  listLockPath={listLockPath}
                  isTripLeader={isTripLeader}
                />
              </div>

              <div className="switcher threshold-180 gap-3">
                {renderStatusBox()}
                {renderLockBox()}
              </div>

              {!isTripLeader && (
                <div className="switcher threshold-180 text-lg border-gray-3 pad-6">
                  <div className="stack gap-1">
                    <h2>New – {capitalize(sectionNounSingular)} numbers and names</h2>
                    <p>
                      Moving forward, each {sectionNounSingular} created in GroupCollect Register will be automatically
                      numbered. Adding names to these {sectionNounPlural} is now totally optional. If previously given names
                      are no longer useful, you can remove them by clicking this button.
                    </p>
                  </div>
                  <div className="center-contents justify-end basis-80 grow-0">
                    <button
                      className="button less-important"
                      onClick={() => {
                        put(this.props.unnamePath, {})
                      }}
                      data-confirm={`Are you sure you want to remove the names of your ${sectionNounPlural}? This action can't be undone.`}>
                      Remove names
                    </button>
                  </div>
                </div>
              )}

              <div className="cluster gap-3 | fg-gray-600">
                <a className="legacy-with-icon svg-icon" onClick={this.toggleExpand()} data-role="expand-all">
                  {this.expandToggleText()}
                </a>

                <div className="grow-1"></div>
                <GlobalPassengerSearchBar
                  allAssignments={allAssignments}
                  sections={sectionCollection}
                  onClickJumpToSection={this.handleJumpToSection}
                />
              </div>
            </section>
            <section className="stack gap-4">{sectionsBody}</section>
          </div>
        )}
      </>
    )
  }

  formattedListType = () => {
    const { list } = this.props
    if (list.listType == "bus_list") {
      return "Bus List"
    } else if (list.listType == "room_list") {
      return "Room List"
    } else if (list.listType == "flight_list") {
      return "Flight List"
    } else if (list.listType == "chaperone_list") {
      return "Chaperone List"
    } else {
      return "Other"
    }
  }

  maybeShowExportButton = () => {
    const { list } = this.props
    if (list.listType == "room_list") {
      return (
        <a
          className="legacy-with-icon svg-icon link-button-component"
          href={this.props.roomListExportPath}
          data-role="export-link">
          <SVG src={downloadSvg} className="" />
          <div>Export</div>
        </a>
      )
    } else if (list.listType == "flight_list") {
      return (
        <a
          className="legacy-with-icon svg-icon link-button-component"
          href={this.props.flightListExportPath}
          data-role="export-link">
          <SVG src={downloadSvg} className="" />
          <div>Export</div>
        </a>
      )
    }
  }

  maybeShowDownloadButton = () => {
    const { list } = this.props
    if (list.listType != "room_list" && list.listType != "flight_list") {
      return (
        <a
          className="legacy-with-icon svg-icon link-button-component"
          href={this.props.participationsExportPath}
          data-role="download-link">
          <SVG src={downloadSvg} className="" />
          <div>Download</div>
        </a>
      )
    }
  }

  expandToggleText = () => {
    if (this.state.allExpanded) {
      return (
        <>
          <SVG src={chevronDownSvg} className="" />
          <div>Collapse All</div>
        </>
      )
    } else {
      return (
        <>
          <SVG src={chevronRightSvg} className="" />
          <div>Expand All</div>
        </>
      )
    }
  }

  toggleExpand = () => {
    return () => {
      if (this.state.allExpanded) {
        this.setState({ ...this.state, expandedSectionIds: [], allExpanded: !this.state.allExpanded })
      } else {
        let ids = this.props.sections.map((section) => section.id)
        this.setState({ ...this.state, expandedSectionIds: ids, allExpanded: !this.state.allExpanded })
      }
    }
  }

  handleExpansionToggle = (section: Section) => {
    if (this.state.expandedSectionIds.includes(section.id)) {
      let remainderIds = this.state.expandedSectionIds.filter((id) => id != section.id)
      if (remainderIds.length == 0) {
        this.setState({ ...this.state, allExpanded: false, expandedSectionIds: [] })
      } else {
        this.setState({ ...this.state, expandedSectionIds: remainderIds })
      }
    } else {
      this.setState({ ...this.state, expandedSectionIds: [section.id] })
    }
  }

  handleJumpToSection = (section: Section) => {
    const sectionRef = this.state.sectionRefs[section.id]

    sectionRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })

    this.setState({ ...this.state, expandedSectionIds: [section.id] })
  }
}

interface SectionStatsProps {
  sectionCollection: SectionCollection
  allAssignments: AssignmentCollection
  packages: Package[]
  sectionNounPlural: string
  list: List
  listLockPath: string
  isTripLeader: boolean
}
interface SectionStatsState {}

@observer
class SectionStats extends React.Component<SectionStatsProps, SectionStatsState> {
  constructor(props) {
    super(props)
    this.state = {}
  }

  render() {
    return (
      <div className="cluster | min-w-full gap-3" data-role="section-stats">
        {this.renderUnassignedPassengersCount()}
        {this.renderSectionCount()}
        {this.renderIncompleteSectionsCount()}
      </div>
    )
  }

  renderUnassignedPassengersCount() {
    const { allAssignments } = this.props
    const unassignedPassengersCount = allAssignments.unassignedAssignments().length

    let maybePassengerBreakdownTooltip
    if (unassignedPassengersCount > 0) {
      maybePassengerBreakdownTooltip = (
        <InfoTooltip>
          <UnassignedPassengersBreakdown {...this.props} />
        </InfoTooltip>
      )
    }

    let highlightedCount
    if (unassignedPassengersCount > 0) {
      highlightedCount = unassignedPassengersCount
    } else {
      highlightedCount = (
        <div className="self-center svg-icon-s">
          <SVG src={checkmarkSvg} className="self-center" />
        </div>
      )
    }

    let textColorClass = "fg-gray-600"
    if (unassignedPassengersCount > 0) {
      textColorClass = "fg-red-700"
    }

    let descriptionText = "All Passengers Assigned"
    if (unassignedPassengersCount > 0) {
      descriptionText = "Unassigned Passengers"
    }

    return (
      <div className={`row | border-gray-200 ${textColorClass} line-1`} data-role="section-stat">
        <div className="pad-3 | bg-gray-50">{highlightedCount}</div>
        <div className="row pad-inline-3 gap-2">
          <span className="whitespace-nowrap">{descriptionText}</span> {maybePassengerBreakdownTooltip}
        </div>
      </div>
    )
  }

  renderIncompleteSectionsCount() {
    const { sectionCollection, sectionNounPlural } = this.props
    const incompleteSectionsCount = sectionCollection.incompleteSections()
    const allSectionsComplete = incompleteSectionsCount == 0 && sectionCollection.length > 0

    let highlightedCount
    if (allSectionsComplete) {
      highlightedCount = (
        <div className="svg-icon-s">
          <SVG src={checkmarkSvg} className="" />
        </div>
      )
    } else {
      highlightedCount = incompleteSectionsCount
    }

    let textColorClass = "fg-red-700"
    if (allSectionsComplete) {
      textColorClass = "fg-gray-600"
    }

    let descriptionText = `Incomplete ${capitalize(sectionNounPlural)}`
    if (allSectionsComplete) {
      descriptionText = `All ${capitalize(sectionNounPlural)} Booked Full`
    }

    return (
      <div className={`row | border-gray-200 ${textColorClass} font-bold line-1`} data-role="section-stat">
        <div className="pad-3 | bg-gray-50">{highlightedCount}</div>
        <div className="flex pad-inline-3">{descriptionText}</div>
      </div>
    )
  }

  renderSectionCount() {
    const { sectionCollection, sectionNounPlural } = this.props
    return (
      <div className={`row | border-gray-200 fg-gray-600 line-1`} data-role="section-stat">
        <div className="box | pad-3 bg-gray-50">{sectionCollection.length}</div>
        <div className="flex | pad-inline-3">{capitalize(sectionNounPlural)}</div>
      </div>
    )
  }
}

interface UnassignedPassengersBreakdownProps {
  allAssignments: AssignmentCollection
  packages: Package[]
}
const UnassignedPassengersBreakdown = observer(({ allAssignments, packages }: UnassignedPassengersBreakdownProps) => {
  const unassignedPassengersByGender = allAssignments.unassignedCountsByGender()
  const unassignedPassengerByPackage = packages.map((p) => [p.name, allAssignments.unassignedWithPackage(p).length])

  return (
    <div className="grid h-auto gap-block-2 p-4 leading-snug text-gray-700 ">
      <div>
        {unassignedPassengersByGender.unassignedMalesCount} unassigned <b>male</b> passenger(s)
      </div>
      <div>
        {unassignedPassengersByGender.unassignedFemalesCount} unassigned <b>female</b> passenger(s)
      </div>
      {unassignedPassengersByGender.unassignedXCount > 0 && (
        <div>
          {unassignedPassengersByGender.unassignedXCount} unassigned <b>X</b> passenger(s)
        </div>
      )}
      {unassignedPassengersByGender.unassignedMissingGenderCount > 0 && (
        <div>
          {unassignedPassengersByGender.unassignedMissingGenderCount} unassigned passenger(s) <b>without a gender set</b>
        </div>
      )}

      {unassignedPassengerByPackage.map(([packageName, unassignedCount]) => (
        <div className="mt-4">
          {unassignedCount} unassigned passenger(s) in the <b>{packageName}</b>
        </div>
      ))}
      {allAssignments.unassignedWithoutPackageSet().length > 0 && (
        <div className="mt-4">
          {allAssignments.unassignedWithoutPackageSet().length} unassigned passenger(s) <b>without a package selected</b>
        </div>
      )}
    </div>
  )
})

interface GlobalPassengerSearchBarProps {
  allAssignments: AssignmentCollection
  sections: SectionCollection
  onClickJumpToSection: (Section) => void
}
interface GlobalPassengerSearchBarState {}

@observer
class GlobalPassengerSearchBar extends React.Component<GlobalPassengerSearchBarProps, GlobalPassengerSearchBarState> {
  constructor(props) {
    super(props)
    this.state = {}
  }

  render() {
    return (
      <div className="search-bar">
        <SearchBar
          placeholder={"Passenger Search..."}
          renderSearchResultRow={this.renderSearchResultRow}
          performLookup={this.performAssignmentSearch}
        />
      </div>
    )
  }

  renderSearchResultRow = (assignment, resetSearchStateFn) => {
    const maybeSection = this.props.sections.findById(assignment.sectionId)

    return (
      <div
        key={assignment.id}
        className="z-50 grid grid-cols-2 bg-white global-search-result-row"
        onClick={() => this.handleSectionLinkClick(maybeSection, resetSearchStateFn)}>
        <div>{assignment.passengerName}</div>

        <div className="grid justify-end grid-flow-col gap-inline-1">
          <div className="font-bold">{this.renderSectionName(maybeSection)}</div>
          <div>{this.renderSectionLinkIcon(maybeSection)}</div>
        </div>
      </div>
    )
  }

  renderSectionName = (section: Section | undefined) => {
    if (section) {
      return <a className="">{section.name}</a>
    } else {
      return <div className="fg-red">Unassigned</div>
    }
  }

  renderSectionLinkIcon = (section: Section | undefined) => {
    if (section) {
      return (
        <a className="svg-icon">
          <SVG src={externalLinkSvg} className="" />
        </a>
      )
    }
  }

  performAssignmentSearch: (string) => Promise<Assignment[]> = (queryStr) => {
    const { allAssignments } = this.props

    return new Promise(function (resolve, _reject) {
      const matchingAssignments = allAssignments.search(queryStr).items
      resolve(matchingAssignments)
    })
  }

  handleSectionLinkClick = (section: Section | undefined, resetSearchStateFn) => {
    if (section) {
      resetSearchStateFn()
      this.props.onClickJumpToSection(section)
    }
  }
}
